import { useEffect, useState } from "react";
import { GraduationCap, Plus, Save, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function AdminEducationCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newCat, setNewCat] = useState({ id: "", label: "", max_amount: "", enabled: true });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/education/categories");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const res = await api.put("/api/admin/education/categories", { items });
      setItems(res.data?.items || []);
      toast.success(`Saved ${res.data?.saved ?? items.length} categories`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addCategory = async () => {
    const cid = (newCat.id || "").trim().toLowerCase();
    const label = (newCat.label || "").trim();
    if (!cid || !label) { toast.error("Both id and label are required"); return; }
    try {
      const payload = {
        id: cid,
        label,
        enabled: newCat.enabled,
        max_amount: newCat.max_amount ? Number(newCat.max_amount) : null,
      };
      const res = await api.post("/api/admin/education/categories", payload);
      setItems(res.data?.items || []);
      setAddOpen(false);
      setNewCat({ id: "", label: "", max_amount: "", enabled: true });
      toast.success(`Added "${label}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const enabledCount = items.filter((it) => it.enabled).length;

  return (
    <div className="space-y-6" data-testid="admin-education-categories">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <GraduationCap className="size-6" />
            Education categories
          </h1>
          <p className="text-sm text-muted-foreground">
            Toggle which fee categories partners can pick when initiating an education-fee payout. Disabled categories are hidden from the partner wizard immediately on save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-btn">
                <Plus className="size-4" /> Add category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add education category</DialogTitle>
                <DialogDescription>
                  Use a kebab-case id (e.g. "competitive-exam"). Label is what partners see.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>ID (kebab-case)</Label>
                  <Input
                    value={newCat.id}
                    placeholder="e.g. competitive-exam"
                    onChange={(e) => setNewCat({ ...newCat, id: e.target.value })}
                    data-testid="new-category-id"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Label</Label>
                  <Input
                    value={newCat.label}
                    placeholder="e.g. Competitive exam fee"
                    onChange={(e) => setNewCat({ ...newCat, label: e.target.value })}
                    data-testid="new-category-label"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Max amount (₹, optional)</Label>
                  <Input
                    type="number"
                    value={newCat.max_amount}
                    placeholder="e.g. 50000"
                    onChange={(e) => setNewCat({ ...newCat, max_amount: e.target.value })}
                    data-testid="new-category-max"
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <span className="text-sm">Enabled on creation</span>
                  <Switch
                    checked={newCat.enabled}
                    onCheckedChange={(v) => setNewCat({ ...newCat, enabled: v })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addCategory} data-testid="confirm-add-category">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={saveAll} disabled={saving || loading} data-testid="save-all-categories-btn">
            <Save className="size-4" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Available categories</span>
            <span className="text-sm font-normal text-muted-foreground">
              {enabledCount}/{items.length} enabled
            </span>
          </CardTitle>
          <CardDescription>
            Edit labels and caps inline. Switching off hides the category from partners on next page load.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No categories configured yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">ID</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead className="w-[160px]">Max amount (₹)</TableHead>
                  <TableHead className="w-[120px]">Enabled</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={it.id || idx} data-testid={`category-row-${it.id}`}>
                    <TableCell className="font-mono text-xs">{it.id}</TableCell>
                    <TableCell>
                      <Input
                        value={it.label || ""}
                        onChange={(e) => updateItem(idx, { label: e.target.value })}
                        data-testid={`category-label-${it.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={it.max_amount ?? ""}
                        onChange={(e) => updateItem(idx, { max_amount: e.target.value ? Number(e.target.value) : null })}
                        data-testid={`category-max-${it.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!it.enabled}
                        onCheckedChange={(v) => updateItem(idx, { enabled: v })}
                        data-testid={`category-toggle-${it.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(idx)}
                        data-testid={`category-delete-${it.id}`}
                      >
                        <Trash2 className="size-4 text-rose-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
